import { Property } from '../types/property';
import { pdf } from '@react-pdf/renderer';
import { ShowcasePDF } from '../components/pdf/ShowcasePDF';
import { ClientPDF } from '../components/pdf/ClientPDF';
import { PrivatePDF } from '../components/pdf/PrivatePDF';
import { ContractPDF } from '../components/pdf/ContractPDF';
import { VisitVoucherPDF, VisitVoucherDataPDF } from '../components/pdf/VisitVoucherPDF';
import React from 'react';

const validateProperty = (property: Property): void => {
  if (!property.id || !property.property_category || !property.address) {
    throw new Error('Property data is incomplete');
  }
};

export const generateShowcasePDF = async (
  property: Property,
  agencyInfo: {
    name: string;
    logo?: string;
    contact: string;
    brandColor?: string;
  },
  options: { orientation?: 'portrait' | 'landscape' } = {}
): Promise<Blob> => {
  try {
    console.log('🚀 generateShowcasePDF - Début pour:', property.id);
    console.log('📋 Paramètres reçus:', { property: property.id, agencyInfo, options });
    
    validateProperty(property);
    console.log('✅ Validation property OK');
    
    if (!agencyInfo.name || !agencyInfo.contact) {
      throw new Error('Agency information is incomplete');
    }
    console.log('✅ Validation agencyInfo OK');

    console.log('📄 Création du composant React PDF...');
    const document = React.createElement(ShowcasePDF, {
      property,
      agencyName: agencyInfo.name.trim(),
      agencyLogo: agencyInfo.logo,
      agencyContact: agencyInfo.contact.trim(),
      brandColor: agencyInfo.brandColor,
      orientation: options.orientation || 'portrait'
    });
    console.log('✅ Composant React créé');

    console.log('🔄 Génération du blob PDF...');
    const blob = await pdf(document).toBlob();
    console.log('📦 Blob généré:', blob ? `${blob.size} bytes` : 'null');
    
    if (!blob) {
      throw new Error('PDF generation failed - no data received');
    }
    
    console.log('🔗 Création de l\'URL blob et ouverture...');
    const url = window.URL.createObjectURL(blob);
    console.log('🌐 URL créée:', url);
    
    try {
      const newWindow = window.open(url, '_blank');
      console.log('🪟 Tentative d\'ouverture:', newWindow ? 'Succès' : 'Bloqué');
      
      if (!newWindow) {
        console.warn('⚠️ Popup bloqué - téléchargement automatique...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `showcase-${property.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('💾 Téléchargement déclenché');
      }
    } catch (error) {
      console.warn('⚠️ Erreur ouverture PDF:', error);
    }
    
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      console.log('🗑️ URL révoquée');
    }, 1000);
    
    console.log('🎉 generateShowcasePDF terminé avec succès');
    return blob;
  } catch (error) {
    console.error('💥 Erreur dans generateShowcasePDF:', error);
    console.error('📋 Type d\'erreur:', typeof error);
    console.error('📊 Message:', error instanceof Error ? error.message : 'Erreur inconnue');
    console.error('🔍 Stack:', error instanceof Error ? error.stack : 'Pas de stack');
    
    const message = error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite';
    throw new Error(`Impossible de générer le PDF showcase: ${message}`);
  }
};

export const generateClientPDF = async (
  property: Property,
  brandColor?: string
): Promise<Blob> => {
  try {
    console.log('🚀 generateClientPDF - Début pour:', property.id);
    validateProperty(property);

    const document = React.createElement(ClientPDF, { property, brandColor });

    const blob = await pdf(document).toBlob();
    if (!blob) {
      throw new Error('PDF generation failed - no data received');
    }
    
    const url = window.URL.createObjectURL(blob);
    
    try {
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        console.warn('⚠️ Popup bloqué - téléchargement automatique...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `client-${property.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.warn('⚠️ Erreur ouverture PDF:', error);
    }
    
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    console.log('🎉 generateClientPDF terminé avec succès');
    return blob;
  } catch (error) {
    console.error('💥 Erreur dans generateClientPDF:', error);
    const message = error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite';
    throw new Error(`Impossible de générer le PDF client: ${message}`);
  }
};

export const generatePrivatePDF = async (
  property: Property,
  brandColor?: string
): Promise<Blob> => {
  try {
    console.log('🚀 generatePrivatePDF - Début pour:', property.id);
    validateProperty(property);

    const document = React.createElement(PrivatePDF, { property, brandColor });

    const blob = await pdf(document).toBlob();
    if (!blob) {
      throw new Error('PDF generation failed - no data received');
    }
    
    const url = window.URL.createObjectURL(blob);
    
    try {
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        console.warn('⚠️ Popup bloqué - téléchargement automatique...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `private-${property.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.warn('⚠️ Erreur ouverture PDF:', error);
    }
    
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    console.log('🎉 generatePrivatePDF terminé avec succès');
    return blob;
  } catch (error) {
    console.error('💥 Erreur dans generatePrivatePDF:', error);
    const message = error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite';
    throw new Error(`Impossible de générer le PDF privé: ${message}`);
  }
};

export const generateContractPDF = async (
  property: Property,
  agencyInfo: {
    name: string;
    address: string;
    brandColor?: string;
  }
): Promise<Blob> => {
  try {
    console.log('🚀 generateContractPDF - Début pour:', property.id);
    validateProperty(property);
    if (!agencyInfo.name || !agencyInfo.address) {
      throw new Error('Agency information is incomplete');
    }

    const document = React.createElement(ContractPDF, {
      property,
      agencyName: agencyInfo.name.trim(),
      agencyAddress: agencyInfo.address.trim(),
      brandColor: agencyInfo.brandColor
    });

    const blob = await pdf(document).toBlob();
    if (!blob) {
      throw new Error('PDF generation failed - no data received');
    }
    
    const url = window.URL.createObjectURL(blob);
    
    try {
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        console.warn('⚠️ Popup bloqué - téléchargement automatique...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `contract-${property.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.warn('⚠️ Erreur ouverture PDF:', error);
    }
    
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    console.log('🎉 generateContractPDF terminé avec succès');
    return blob;
  } catch (error) {
    console.error('💥 Erreur dans generateContractPDF:', error);
    const message = error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite';
    throw new Error(`Impossible de générer le PDF contrat: ${message}`);
  }
};

export const generateVisitVoucherPDF = async (
  property: Property,
  agencyInfo: {
    name: string;
    address: string;
    phone: string;
    brandColor?: string;
  },
  visitData?: VisitVoucherDataPDF
): Promise<Blob> => {
  try {
    console.log('🚀 generateVisitVoucherPDF - Début pour:', property.id);
    console.log('📋 Données visiteur reçues:', visitData);
    
    validateProperty(property);
    if (!agencyInfo.name || !agencyInfo.address || !agencyInfo.phone) {
      throw new Error('Agency information is incomplete');
    }

    const document = React.createElement(VisitVoucherPDF, {
      property,
      agencyName: agencyInfo.name.trim(),
      agencyAddress: agencyInfo.address.trim(),
      agencyPhone: agencyInfo.phone.trim(),
      brandColor: agencyInfo.brandColor,
      visitData: visitData
    });

    const blob = await pdf(document).toBlob();
    if (!blob) {
      throw new Error('PDF generation failed - no data received');
    }
    
    const url = window.URL.createObjectURL(blob);
    
    try {
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        console.warn('⚠️ Popup bloqué - téléchargement automatique...');
        const link = document.createElement('a');
        link.href = url;
        link.download = `bon-visite-${property.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.warn('⚠️ Erreur ouverture PDF:', error);
    }
    
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    console.log('🎉 generateVisitVoucherPDF terminé avec succès');
    return blob;
  } catch (error) {
    console.error('💥 Erreur dans generateVisitVoucherPDF:', error);
    const message = error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite';
    throw new Error(`Impossible de générer le bon de visite: ${message}`);
  }
};
